/// <amd-module name="Repos/Tfvc/Views/FilesHubActions/TfvcFilesHubActionsService" />
import * as React from "react";
import { ItemContentType, TfvcChange, TfvcChangeset, TfvcChangesetRef, VersionControlChangeType } from "Repos/Common/Contracts/Git";
import { getShortCommitComment } from "Repos/Common/Util/Commit";
import { getFileName, getParentPath } from "Repos/Common/Util/File";
import { IVCViewModelDataProviderService } from "Repos/Common/VCViewModelDataProviderService";
import { IReposMessagesService } from "Repos/Messages/ReposMessagesService";
import { getTfvcRoutingService } from "Repos/Tfvc/Common/TfvcRoutingService";
import { getFilesHubViewService, ITfvcItem } from "Repos/Tfvc/Views/FilesHub/FilesHubViewService";
import * as Resources from "Repos/Tfvc/Views/FilesHubActions/Resources";
import { ISecurityPanelProps } from "TFS/Security/Common/Security.Controls.Props";
import { IReadyableReadonlyObservableArray, ObservableValue } from "VSS/Core/Observable";
import { equals, format } from "VSS/Core/Util/String";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssLayoutManager, WrappedComponent } from "VSS/Platform/Layout";
import { toLookup } from "VSS/Platform/Util/Array";
import { combineUrlPaths } from "VSS/Platform/Util/Url";
import { FileEncoding } from "VSSUI/FileInput";
import { Icon } from "VSSUI/Icon";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import {
    CheckinChangesPanel,
    CheckinCompleteListener,
    GatedCheckinResponse,
    ICheckinChangesContext,
    ICheckinChangesDetails
} from "./Components/CheckinChangesPanel";
import { NewFileDialog } from "./Components/NewFileDialog";
import { getTfvcClient } from "./RestClient/Tfvc";

const tfvcRoot = "$/";

/**
 * Service for performing actions on items in the Files hub
 */
export interface ITfvcFilesHubActionsService extends IVssService {
    checkin(item: ITfvcItem, content: string): void;
    delete(item: ITfvcItem): void;
    newFile(parentItem: ITfvcItem): void;
    newFolder(parentItem: ITfvcItem): void;
    rename(item: ITfvcItem): void;
    security(item: ITfvcItem): void;
    upload(parentItem: ITfvcItem): void;
}

class TfvcFilesHubActionsService extends VssService implements ITfvcFilesHubActionsService {
    public checkin(item: ITfvcItem, content: string): void {
        this.handleFileAction(
            format(item.isNewItem ? Resources.AddedFileCommentFormat : Resources.UpdatedCommentFormat, item.name),
            undefined,
            undefined,
            undefined,
            undefined,
            details => this.performEditFile(item, details, content),
            (details, changesetRef, error) => {
                this.showCommitMessage(details, changesetRef, error);

                // Clear the editing context's dirty state
                const filesService = getFilesHubViewService(this.pageContext);
                const itemContent = filesService.getSelectedItemContent();
                if (itemContent.value && itemContent.value.editContext) {
                    itemContent.value.editContext.setContent(itemContent.value.editContext.getContent(), true);
                }
                filesService.setEditMode(false);

                FastPageSwitch(this.pageContext, filesService.getItemUrl(item.path), false);
            }
        );
    }

    public delete(item: ITfvcItem): void {
        this.handleFileAction(
            format(Resources.DeletedCommentFormat, item.name),
            undefined,
            undefined,
            undefined,
            undefined,
            details => {
                return this.performDelete(item, details);
            },
            (details, changesetRef, error) => {
                this.showCommitMessage(details, changesetRef, error);

                // Navigate to the parent item
                const filesService = getFilesHubViewService(this.pageContext);
                let parentPath = getParentPath(item.path, tfvcRoot);
                while (parentPath && filesService.getChildItems(parentPath).length < 2) {
                    // We may have deleted the last item in a folder, keep going up until we hit the root.
                    parentPath = getParentPath(parentPath, tfvcRoot);
                }

                FastPageSwitch(this.pageContext, filesService.getItemUrl(parentPath || tfvcRoot), false);
            }
        );
    }

    public newFile(parentItem: ITfvcItem): void {
        this.handleNewFile(parentItem);
    }

    public newFolder(parentItem: ITfvcItem): void {
        let newPath: string;
        this.handleFileAction(
            Resources.AddedNewFolderComment,
            undefined,
            undefined,
            undefined,
            parentItem,
            details => {
                newPath = combineUrlPaths(parentItem.path, details.newFolderPath!);
                return this.performNewFolder(parentItem, newPath, details.comment);
            },
            (details, changesetRef, error) => {
                this.showCommitMessage(details, changesetRef, error);

                const filesService = getFilesHubViewService(this.pageContext);
                FastPageSwitch(this.pageContext, filesService.getItemUrl(newPath), true);
            }
        );
    }

    public rename(item: ITfvcItem): void {
        const itemName = getFileName(item.path);
        let newPath: string;
        this.handleFileAction(
            format(Resources.RenameCommentFormat, itemName, itemName),
            itemName,
            undefined,
            undefined,
            undefined,
            details => {
                const parentPath = getParentPath(item.path, tfvcRoot) || tfvcRoot;
                newPath = combineUrlPaths(parentPath, details.renameTo!);
                return this.performRename(item, newPath, details.comment);
            },
            (details, changesetRef, error) => {
                this.showCommitMessage(details, changesetRef, error);

                const filesService = getFilesHubViewService(this.pageContext);
                FastPageSwitch(this.pageContext, filesService.getItemUrl(newPath), false);
            }
        );
    }

    public security(item: ITfvcItem): void {
        const vcViewModelDataProviderService = this.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
        const permissionSetId = vcViewModelDataProviderService.getViewModel().repositoryPermissionSet;

        this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout(onCancel => (
            <WrappedComponent<ISecurityPanelProps>
                dependencies={["ms.vss-admin-web.security-view-common-content"]}
                wrappedType="ms.vss-admin-web.security-panel-component"
                wrappedProps={{
                    onCancel,
                    permissionSetId,
                    permissionSetToken: item.path,
                    securityViewTitle: format(Resources.PermissionTextFormat, item.path),
                    resourceId: item.path,
                    resourceType: "Tfvc"
                }}
            />
        ));
    }

    public upload(parentItem: ITfvcItem, initialFiles?: FileList): void {
        this.handleFileAction(
            "",
            undefined,
            parentItem,
            initialFiles,
            undefined,
            details => {
                return this.performUpload(parentItem, details);
            },
            (details, changesetRef, error) => {
                this.showCommitMessage(details, changesetRef, error);

                const filesService = getFilesHubViewService(this.pageContext);
                FastPageSwitch(this.pageContext, filesService.getItemUrl(parentItem.path), false);
            }
        );
    }

    private handleFileAction(
        initialComment: string,
        renameFrom: string | undefined,
        uploadTo: ITfvcItem | undefined,
        initialUploadFiles: FileList | undefined,
        newFolderParent: ITfvcItem | undefined,
        performAction: (details: ICheckinChangesDetails) => Promise<TfvcChangesetRef>,
        postActionNavigate: (details: ICheckinChangesDetails, changesetRef: TfvcChangesetRef, error: Error | undefined) => void
    ): void {
        const filesService = getFilesHubViewService(this.pageContext);
        const errorMessage = new ObservableValue("");
        const gatedCheckin = new ObservableValue<GatedCheckinResponse | undefined>(undefined);
        const showOverlay = new ObservableValue(false);

        const listeners: CheckinCompleteListener[] = [];
        const context: ICheckinChangesContext = {
            addCheckinCompleteListener: (listener: CheckinCompleteListener): void => {
                listeners.push(listener);
            }
        };

        this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout((onDismiss: () => void) => {
            const onDismissPanel = async (details: ICheckinChangesDetails | undefined) => {
                if (details) {
                    errorMessage.value = "";
                    showOverlay.value = true;

                    let result: TfvcChangesetRef;
                    try {
                        result = await performAction(details);
                    } catch (ex) {
                        showOverlay.value = false;

                        if (ex.serverError && ex.serverError.isGatedCheckin) {
                            gatedCheckin.value = ex.serverError as GatedCheckinResponse;
                        } else {
                            errorMessage.value = ex.message;
                        }

                        return;
                    }

                    const promises: Promise<void>[] = [];
                    for (const listener of listeners) {
                        const listenerResult = listener(result);
                        if (listenerResult && listenerResult.then) {
                            promises.push(listenerResult);
                        }
                    }

                    let error: Error | undefined;
                    if (promises.length) {
                        try {
                            await Promise.all(promises);
                        } catch (ex) {
                            error = ex;
                        }
                    }

                    // Dimiss the panel
                    onDismiss();

                    postActionNavigate(details, result, error);
                } else {
                    onDismiss();
                }
            };

            return (
                <CheckinChangesPanel
                    comment={initialComment}
                    checkinChangesContext={context}
                    errorMessage={errorMessage}
                    gatedCheckin={gatedCheckin}
                    initialFiles={initialUploadFiles}
                    newFolderParentPath={newFolderParent && newFolderParent.path}
                    onDismiss={onDismissPanel}
                    renameFrom={renameFrom}
                    showOverlay={showOverlay}
                    uploadExistingItems={uploadTo && (filesService.getChildItems(uploadTo.path) as IReadyableReadonlyObservableArray<ITfvcItem>)}
                    uploadTo={uploadTo && uploadTo.path}
                />
            );
        });
    }

    private async handleNewFile(parentItem: ITfvcItem) {
        const errorMessage = new ObservableValue("");
        this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout((onDismiss: () => void) => {
            const onDismissDialog = async (name: string | undefined) => {
                if (name) {
                    const filesService = getFilesHubViewService(this.pageContext);

                    // Make sure that the path doesn't already exist
                    const path = combineUrlPaths(parentItem.path, name);
                    const parentPath = getParentPath(path, tfvcRoot)!;

                    const existingItems = filesService.getChildItems(parentPath);
                    if (existingItems.value.some(i => equals(i.path, path, true))) {
                        errorMessage.value = format(Resources.NewFileAlreadyExists, path);
                        return;
                    }

                    // Dimiss the panel
                    onDismiss();

                    // Navigate to the temporary new path, user will check in once new content is ready
                    const newUrl = filesService.getItemUrl(path) + "&createIfNew=true";
                    FastPageSwitch(this.pageContext, newUrl, true);
                } else {
                    onDismiss();
                }
            };

            return <NewFileDialog errorMessage={errorMessage} onDismiss={onDismissDialog} parentPath={parentItem.path} />;
        });
    }

    private async performDelete(item: ITfvcItem, details: ICheckinChangesDetails) {
        return getTfvcClient(this.pageContext).createChangeset({
            changes: [
                {
                    changeType: VersionControlChangeType.Delete,
                    item: {
                        path: item.path,
                        version: item.changesetId
                    }
                }
            ],
            comment: details.comment
        } as TfvcChangeset);
    }

    private async performEditFile(item: ITfvcItem, details: ICheckinChangesDetails, content: string) {
        return getTfvcClient(this.pageContext).createChangeset({
            changes: [
                {
                    changeType: item.isNewItem ? VersionControlChangeType.Add : VersionControlChangeType.Edit,
                    item: {
                        path: item.path,
                        contentMetadata: {
                            encoding: item.encoding || TfvcEncodingConstants.UTF8
                        },
                        version: item.changesetId || 0
                    },
                    newContent: {
                        content: content,
                        contentType: ItemContentType.RawText
                    }
                }
            ],
            comment: details.comment
        } as TfvcChangeset);
    }

    private async performNewFolder(parentItem: ITfvcItem, path: string, comment: string) {
        return getTfvcClient(this.pageContext).createChangeset({
            changes: [
                {
                    changeType: VersionControlChangeType.Add,
                    item: {
                        path,
                        isFolder: true,
                        version: parentItem.changesetId
                    }
                }
            ],
            comment
        } as TfvcChangeset);
    }

    private async performRename(item: ITfvcItem, newPath: string, comment: string) {
        return getTfvcClient(this.pageContext).createChangeset({
            changes: [
                {
                    changeType: VersionControlChangeType.Rename,
                    sourceServerItem: item.path,
                    item: {
                        path: newPath,
                        version: item.changesetId
                    }
                }
            ],
            comment
        } as TfvcChangeset);
    }

    private async performUpload(parentItem: ITfvcItem, { comment, files }: ICheckinChangesDetails) {
        const filesService = getFilesHubViewService(this.pageContext);
        const existingItems = toLookup(filesService.getChildItems(parentItem.path).value as ITfvcItem[], i => i.name.toLocaleLowerCase());

        const changes: TfvcChange[] = [];
        for (const file of files!) {
            const existingItem = existingItems[file.name.toLocaleLowerCase()];
            changes.push({
                changeType: existingItem && !existingItem.isFolder ? VersionControlChangeType.Edit : VersionControlChangeType.Add,
                item: {
                    path: existingItem ? existingItem.path : combineUrlPaths(parentItem.path, file.name),
                    contentMetadata: {
                        encoding: getTfvcEncoding(file.encoding)
                    },
                    version: existingItem ? existingItem.changesetId : 0
                },
                newContent: {
                    content: file.content,
                    contentType: ItemContentType.Base64Encoded
                }
            } as TfvcChange);
        }

        return getTfvcClient(this.pageContext).createChangeset({ changes, comment } as TfvcChangeset);
    }

    private showCommitMessage(details: ICheckinChangesDetails, changesetRef: TfvcChangesetRef, error: Error | undefined) {
        const routingService = getTfvcRoutingService(this.pageContext);
        const changesetUrl = routingService.getChangesetDetailsUrl(changesetRef.changesetId);
        const changesetLink = (
            <span>
                <Icon iconName="BranchCommit" className="margin-right-4" />
                <FPSLink href={changesetUrl}>{changesetRef.changesetId + ": " + getShortCommitComment(details.comment)}</FPSLink>
            </span>
        );

        const messagesService = this.pageContext.getService<IReposMessagesService>("IReposMessagesService");
        messagesService.showMessage(
            {
                renderMessage: () => (
                    <>
                        <FormatComponent elementType="span" format={Resources.ChangesetPushedMessageFormat}>
                            {changesetLink}
                        </FormatComponent>
                        {error ? <span>{error.message}</span> : null}
                    </>
                ),
                severity: MessageCardSeverity.Info
            },
            true
        );
    }
}

const enum TfvcEncodingConstants {
    UTF8 = 65001,
    UTF16_BE = 1201,
    UTF16_LE = 1200,
    UTF32_BE = 12001,
    UTF32_LE = 12000,
    Binary = -1,
    Unchanged = -2
}

function getTfvcEncoding(encoding: FileEncoding | undefined): number {
    switch (encoding) {
        case FileEncoding.Binary:
            return TfvcEncodingConstants.Binary;

        case FileEncoding.UTF16_BE:
            return TfvcEncodingConstants.UTF16_BE;

        case FileEncoding.UTF16_LE:
            return TfvcEncodingConstants.UTF16_LE;

        case FileEncoding.UTF32_BE:
            return TfvcEncodingConstants.UTF32_BE;

        case FileEncoding.UTF32_LE:
            return TfvcEncodingConstants.UTF32_LE;

        default:
            return TfvcEncodingConstants.UTF8;
    }
}

Services.add("ITfvcFilesHubActionsService", { serviceFactory: TfvcFilesHubActionsService });
